import i18next from 'i18next';
import type { RigData, SpeciesData, TechniqueData } from '../../types';
import { inferSpeciesTargetability } from '../../utils/speciesTargetability';

export type RecommendationPlacement = 'onboarding_result' | 'species_detail' | 'technique_detail' | 'rig_detail' | 'location_detail' | 'planner_featured';
export type RecommendationBudgetBand = 'budget' | 'mid' | 'premium';
export type RecommendationAccessType = 'shore' | 'boat' | 'both';
export type RecommendationReviewStatus = 'curated' | 'needs_review' | 'fallback';
export type RecommendationSourceType = 'curated_catalog' | 'heuristic_fallback' | 'manual_review_hold';
export type SpeciesFamily = 'sea_bream' | 'sea_bass' | 'pelagic' | 'cephalopod' | 'demersal' | 'generalist';

export interface RecommendationMerchantListing {
  provider: 'amazon';
  marketplace: 'amazon.it' | 'amazon.es' | 'amazon.fr' | 'amazon.co.uk' | 'amazon.de';
  asin?: string;
  url?: string;
  title?: string;
  brand?: string;
  imageUrl?: string;
  priceLabel?: string;
  badge?: string;
  availability?: string;
  verifiedAt?: string;
  note?: string;
}

export type RecommendationSetupSlot =
  | 'main_line'
  | 'leader'
  | 'hook'
  | 'weight'
  | 'float'
  | 'jig'
  | 'egi'
  | 'clip'
  | 'spare';

export interface RecommendationCompleteSetupItem {
  slot: RecommendationSetupSlot;
  label: string;
  value: string;
  note?: string;
  reviewed: boolean;
  setupGuideId?: string;
  merchantItemIds?: string[];
}

export interface RecommendationCompleteSetup {
  summary?: string;
  items: RecommendationCompleteSetupItem[];
  confidence: 'high' | 'medium' | 'low';
  reviewed: boolean;
}

export interface RecommendationItem {
  id: string;
  title: string;
  description: string;
  rationale: string;
  query: string;
  merchantName: string;
  ctaLabel: string;
  tags: string[];
  listing?: RecommendationMerchantListing;
  merchantUrl?: string;
  merchantLinkMode?: 'amazon_listing' | 'amazon_search';
  merchantMarketplace?: string;
  merchantDisplayName?: string;
}

interface RecommendationMatchProfile {
  techniqueIds?: string[];
  rigIds?: string[];
  speciesIds?: string[];
  accessTypes?: RecommendationAccessType[];
  budgetBands?: RecommendationBudgetBand[];
  speciesFamilies?: SpeciesFamily[];
}

export interface RecommendationCatalogBundle {
  id: string;
  title: string;
  subtitle: string;
  reviewStatus: RecommendationReviewStatus;
  qualityScore: number;
  confidenceLabel: 'high' | 'medium' | 'low';
  provenance: string[];
  coverageNote: string;
  commerceEnabled: boolean;
  reviewMessage?: string;
  completeSetup?: RecommendationCompleteSetup;
  match: RecommendationMatchProfile;
  items: RecommendationItem[];
}

export interface RecommendationCatalogContext {
  placement: RecommendationPlacement;
  species?: SpeciesData | null;
  technique?: TechniqueData | null;
  rig?: RigData | null;
  budgetBand: RecommendationBudgetBand;
  accessType: RecommendationAccessType;
}

export interface ResolvedRecommendationCatalog {
  bundle: RecommendationCatalogBundle;
  sourceType: RecommendationSourceType;
  matchedBy: string[];
}

const DEFAULT_MERCHANT_NAME = 'Amazon';
const catalogText = (key: string, options?: Record<string, unknown>) => String(i18next.t(`codex:recommendationCatalog.${key}`, options));

const MANUAL_REVIEW_HOLD_KEYS: Record<string, string> = {
  spearfishing: 'manualHolds.spearfishing'
};


export const isTechniqueManualReviewHold = (techniqueId?: string | null): boolean =>
  Boolean(techniqueId && MANUAL_REVIEW_HOLD_KEYS[techniqueId]);

const referenceOnlySpeciesMessage = () => catalogText('species.referenceOnly');
const incidentalSpeciesMessage = () => catalogText('species.incidental');

const inferSpeciesFamily = (species?: SpeciesData | null): SpeciesFamily | null => {
  if (!species) {
    return null;
  }

  const haystack = `${species.name} ${species.scientificName} ${species.speciesId}`.toLowerCase();

  if (/diplodus|sparus|pagrus|bream|porgy|steenbras/.test(haystack)) {
    return 'sea_bream';
  }

  if (/dicentrarchus|sea bass/.test(haystack)) {
    return 'sea_bass';
  }

  if (/thunnus|tuna|bonito|amberjack|mackerel|seriola/.test(haystack)) {
    return 'pelagic';
  }

  if (/octopus|squid|cuttlefish|sepia|loligo/.test(haystack)) {
    return 'cephalopod';
  }

  if (/scorpionfish|hake|dentex|dory|grouper|conger|sole|plaice/.test(haystack)) {
    return 'demersal';
  }

  return 'generalist';
};

const sanitizeSearchTerm = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/[^a-z0-9+\-/. ]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const fallbackRecommendationCatalog: RecommendationCatalogBundle[] = [];


const buildManualReviewHoldBundle = (
  context: RecommendationCatalogContext,
  reviewMessage: string
): RecommendationCatalogBundle => {
  const holdAnchor = context.placement === 'species_detail' && context.species
    ? context.species.speciesId
    : context.placement === 'rig_detail' && context.rig
      ? context.rig.rigId
      : context.placement === 'technique_detail' && context.technique
        ? context.technique.techniqueId
        : context.technique?.techniqueId || context.species?.speciesId || context.rig?.rigId || context.placement;

  return {
    id: `manual_review_hold_${holdAnchor}`,
    title: catalogText('manualHold.title'),
    subtitle: catalogText('manualHold.subtitle'),
    reviewStatus: 'needs_review',
    qualityScore: 18,
    confidenceLabel: 'low',
    provenance: [
      catalogText('manualHold.provenance.triggered', { hold: holdAnchor }),
      catalogText('manualHold.provenance.noLinks'),
      catalogText('manualHold.provenance.validation')
    ],
    coverageNote: catalogText('manualHold.coverageNote'),
    commerceEnabled: false,
    reviewMessage,
    match: {},
    items: []
  };
};

const buildFallbackBundle = (
  context: RecommendationCatalogContext,
  speciesFamily: SpeciesFamily | null
): RecommendationCatalogBundle => {
  const speciesName = context.species?.name || catalogText('fallback.defaults.species');
  const techniqueName = context.technique?.name || catalogText('fallback.defaults.technique');
  const rigName = context.rig?.name || catalogText('fallback.defaults.rig');
  const hookType = context.rig?.components.hooks[0]?.type || catalogText('fallback.defaults.hooks');
  const weightType = context.rig?.components.weights[0]?.type || catalogText('fallback.defaults.sinkers');
  const lineMaterial = context.rig?.components.lines[0]?.material || catalogText('fallback.defaults.line');
  const accessory = context.rig?.components.accessories[0]?.type || catalogText('fallback.defaults.swivels');

  const archetype = (() => {
    const haystack = `${context.technique?.name || ''} ${context.technique?.category || ''} ${context.rig?.name || ''} ${context.rig?.category || ''}`.toLowerCase();
    if (/surf/.test(haystack)) return 'surf';
    if (/spinning|lure|casting/.test(haystack)) return 'spinning';
    if (/trolling/.test(haystack)) return 'trolling';
    if (/jig/.test(haystack)) return 'jigging';
    if (/live bait/.test(haystack)) return 'live_bait';
    return 'bottom';
  })();

  const budgetLabel = context.budgetBand === 'mid' ? catalogText('fallback.query.midRange') : context.budgetBand;
  const accessLabel = context.accessType === 'both' ? 'shore boat' : context.accessType;

  const requiredGear = (context.technique?.equipment.required || []).map(sanitizeSearchTerm).filter(Boolean);
  const optionalGear = (context.technique?.equipment.optional || []).map(sanitizeSearchTerm).filter(Boolean);

  const coreQuery = (() => {
    if (requiredGear.length > 0) {
      return `${requiredGear.slice(0, 2).join(' ')} saltwater mediterranean`.trim();
    }

    switch (archetype) {
      case 'surf':
        return `${budgetLabel} surfcasting rod reel combo mediterranean saltwater ${accessLabel}`;
      case 'spinning':
        return `${budgetLabel} shore spinning rod reel combo mediterranean saltwater ${accessLabel}`;
      case 'trolling':
        return `${budgetLabel} trolling rod reel combo mediterranean saltwater boat`;
      case 'jigging':
        return `${budgetLabel} saltwater jigging rod reel combo mediterranean boat`;
      case 'live_bait':
        return `${budgetLabel} live bait rod reel combo mediterranean saltwater`;
      default:
        return `${budgetLabel} bottom fishing rod reel combo mediterranean saltwater ${accessLabel}`;
    }
  })();

  const supportQuery = (() => {
    if (requiredGear.length > 2) {
      return `${requiredGear.slice(2, 5).join(' ')} saltwater mediterranean`.trim();
    }

    if (optionalGear.length > 0) {
      return `${optionalGear.slice(0, 3).join(' ')} saltwater mediterranean`.trim();
    }

    return `${lineMaterial} leader ${hookType} ${weightType} ${accessory} saltwater fishing`;
  })();

  const consumablesQuery = (() => {
    if (optionalGear.length > 0) {
      return `${optionalGear.slice(0, 3).join(' ')} saltwater mediterranean accessories`.trim();
    }
    switch (archetype) {
      case 'surf':
        return `surfcasting hooks sinkers bait set ${speciesName}`;
      case 'spinning':
        return `saltwater spinning lures set ${speciesName}`;
      case 'trolling':
        return `saltwater trolling lures set ${speciesName}`;
      case 'jigging':
        return `saltwater metal jigs set ${speciesName}`;
      case 'live_bait':
        return `live bait fishing hooks swivels set ${speciesName}`;
      default:
        return `bottom fishing hooks leaders sinkers set ${speciesName}`;
    }
  })();

  return {
    id: 'fallback_generic_bundle',
    title: catalogText('fallback.title'),
    subtitle: catalogText('fallback.subtitle'),
    reviewStatus: 'fallback',
    qualityScore: 56,
    confidenceLabel: 'low',
    provenance: [
      catalogText('fallback.provenance.generated'),
      catalogText('fallback.provenance.noMatch'),
      catalogText('fallback.provenance.needsReview')
    ],
    coverageNote: catalogText('fallback.coverageNote'),
    commerceEnabled: true,
    match: {},
    items: [
      {
        id: 'core_setup',
        title: requiredGear.length > 0 ? catalogText('fallback.items.core.requiredTitle') : catalogText('fallback.items.core.comboTitle'),
        description: requiredGear.length > 0
          ? catalogText('fallback.items.core.requiredDescription', { gear: requiredGear.slice(0, 3).join(', ') })
          : catalogText('fallback.items.core.comboDescription', { budget: budgetLabel, technique: techniqueName.toLowerCase() }),
        rationale: requiredGear.length > 0
          ? catalogText('fallback.items.core.requiredRationale')
          : catalogText('fallback.items.core.comboRationale', { family: speciesFamily || catalogText('fallback.defaults.generalFamily') }),
        query: coreQuery,
        merchantName: DEFAULT_MERCHANT_NAME,
        ctaLabel: requiredGear.length > 0 ? catalogText('fallback.items.core.requiredCta') : catalogText('fallback.items.core.comboCta'),
        tags: ['fallback', context.accessType, context.technique?.category || archetype]
      },
      {
        id: 'terminal_tackle',
        title: context.rig ? catalogText('fallback.items.support.buildTitle', { rig: rigName }) : requiredGear.length > 2 ? catalogText('fallback.items.support.bundleTitle') : catalogText('fallback.items.support.terminalTitle'),
        description: context.rig
          ? catalogText('fallback.items.support.rigDescription', { rig: rigName.toLowerCase() })
          : requiredGear.length > 2
            ? catalogText('fallback.items.support.requiredDescription', { gear: requiredGear.slice(2, 5).join(', ') })
            : catalogText('fallback.items.support.leanDescription', { species: speciesName.toLowerCase() }),
        rationale: requiredGear.length > 0
          ? catalogText('fallback.items.support.requiredRationale')
          : catalogText('fallback.items.support.leanRationale'),
        query: supportQuery,
        merchantName: DEFAULT_MERCHANT_NAME,
        ctaLabel: requiredGear.length > 0 ? catalogText('fallback.items.support.supportCta') : context.rig ? catalogText('fallback.items.support.rigCta') : catalogText('fallback.items.support.tackleCta'),
        tags: ['fallback', hookType, lineMaterial, weightType]
      },
      {
        id: 'bait_or_lures',
        title: optionalGear.length > 0 ? catalogText('fallback.items.extras.optionalTitle') : /spinning|jig|trolling/.test(archetype) ? catalogText('fallback.items.extras.lureTitle') : catalogText('fallback.items.extras.baitTitle'),
        description: optionalGear.length > 0
          ? catalogText('fallback.items.extras.optionalDescription', { gear: optionalGear.slice(0, 3).join(', ') })
          : catalogText('fallback.items.extras.consumablesDescription', { species: speciesName.toLowerCase(), technique: techniqueName.toLowerCase() }),
        rationale: optionalGear.length > 0
          ? catalogText('fallback.items.extras.optionalRationale')
          : catalogText('fallback.items.extras.bridgeRationale'),
        query: consumablesQuery,
        merchantName: DEFAULT_MERCHANT_NAME,
        ctaLabel: optionalGear.length > 0 ? catalogText('fallback.items.extras.optionalCta') : catalogText('fallback.items.extras.consumablesCta'),
        tags: ['fallback', speciesName, archetype]
      }
    ]
  };
};

const scoreBundle = (bundle: RecommendationCatalogBundle, context: RecommendationCatalogContext, speciesFamily: SpeciesFamily | null): { score: number; matchedBy: string[] } => {
  let score = 0;
  const matchedBy: string[] = [];

  if (context.technique && bundle.match.techniqueIds?.includes(context.technique.techniqueId)) {
    score += 80;
    matchedBy.push(`technique:${context.technique.techniqueId}`);
  }

  if (context.rig && bundle.match.rigIds?.includes(context.rig.rigId)) {
    score += 75;
    matchedBy.push(`rig:${context.rig.rigId}`);
  }

  if (context.species && bundle.match.speciesIds?.includes(context.species.speciesId)) {
    score += 70;
    matchedBy.push(`species:${context.species.speciesId}`);
  }

  if (speciesFamily && bundle.match.speciesFamilies?.includes(speciesFamily)) {
    score += 30;
    matchedBy.push(`species_family:${speciesFamily}`);
  }

  if (bundle.match.accessTypes?.includes(context.accessType)) {
    score += 15;
    matchedBy.push(`access:${context.accessType}`);
  }

  if (bundle.match.budgetBands?.includes(context.budgetBand)) {
    score += 10;
    matchedBy.push(`budget:${context.budgetBand}`);
  }

  return { score, matchedBy };
};

export const resolveRecommendationCatalog = (
  context: RecommendationCatalogContext,
  catalogEntries: RecommendationCatalogBundle[] = fallbackRecommendationCatalog
): ResolvedRecommendationCatalog => {
  const speciesFamily = inferSpeciesFamily(context.species);
  const manualReviewTechniqueId = (context.technique?.techniqueId && MANUAL_REVIEW_HOLD_KEYS[context.technique.techniqueId]
    ? context.technique.techniqueId
    : undefined)
    || context.rig?.suitableTechniqueIds.find((techniqueId) => Boolean(MANUAL_REVIEW_HOLD_KEYS[techniqueId]));
  const manualReviewMessage = manualReviewTechniqueId ? catalogText(MANUAL_REVIEW_HOLD_KEYS[manualReviewTechniqueId]) : undefined;
  const speciesTargetability = context.species ? inferSpeciesTargetability(context.species) : null;

  if (speciesTargetability === 'reference_only') {
    return {
      bundle: buildManualReviewHoldBundle(context, referenceOnlySpeciesMessage()),
      sourceType: 'manual_review_hold',
      matchedBy: context.species ? [`species:${context.species.speciesId}`, 'species_targetability:reference_only'] : ['species_targetability:reference_only']
    };
  }

  if (speciesTargetability === 'incidental' && context.placement === 'species_detail') {
    return {
      bundle: buildManualReviewHoldBundle(context, incidentalSpeciesMessage()),
      sourceType: 'manual_review_hold',
      matchedBy: context.species ? [`species:${context.species.speciesId}`, 'species_targetability:incidental'] : ['species_targetability:incidental']
    };
  }

  if (manualReviewMessage) {
    return {
      bundle: buildManualReviewHoldBundle(context, manualReviewMessage),
      sourceType: 'manual_review_hold',
      matchedBy: manualReviewTechniqueId
        ? [`technique:${manualReviewTechniqueId}`, 'review_status:manual_hold']
        : ['review_status:manual_hold']
    };
  }

  const ranked = catalogEntries
    .map((bundle) => ({ bundle, ...scoreBundle(bundle, context, speciesFamily) }))
    .sort((left, right) => right.score - left.score);

  const best = ranked[0];
  if (best && best.score >= 70) {
    return {
      bundle: best.bundle,
      sourceType: 'curated_catalog',
      matchedBy: best.matchedBy
    };
  }

  return {
    bundle: buildFallbackBundle(context, speciesFamily),
    sourceType: 'heuristic_fallback',
    matchedBy: speciesFamily ? [`species_family:${speciesFamily}`] : []
  };
};
